import { useState, useEffect, useMemo, type MouseEvent } from "react";
import { Link } from "react-router-dom";
import { ConnectButton } from '@rainbow-me/rainbowkit';
import { useAccount, useConfig, useReadContract, useWriteContract } from 'wagmi';
import { waitForTransactionReceipt } from 'wagmi/actions';
import { formatEther } from 'viem';
import { AppSidebar } from "@/components/app-sidebar";
import { RiImageLine, RiLoader4Line, RiWallet3Line, RiCoinsLine, RiCheckLine, RiArrowDownSLine, RiSwapLine, RiArrowRightLine } from "@remixicon/react";
import { generateRewardKeys, formatRewardKey, groupRewardKeysByYear, getMonthName } from '@/utils/nft-rewards';
import type { RewardSummary } from '@/abis/nft-dao-utils';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/collapsible';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/breadcrumb";
import { Separator } from "@/components/separator";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/sidebar";
import { Button } from "@/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/card";
import { Badge } from "@/components/badge";
import { NFT_DAO_UTILS_CONTRACT_CONFIG, NFT_DAO_V2_MAIN_CONTRACT_CONFIG } from '@/configs/nft.config';
import { NFT_DAO_V2_MAIN_ABI } from '@/abis/nft-dao-v2-main';
import { ERC721_APPROVAL_ABI } from '@/abis/erc721-approval';
import type { NftData } from '@/abis/nft-dao-utils';
import NftDetailSidebar from '@/components/nft-detail-sidebar';

const ZERO = '0x0000000000000000000000000000000000000000' as const;

export default function NftV1Page() {
  const { address: userAccount } = useAccount();
  const wagmiConfig = useConfig();
  const { writeContractAsync, isPending: isWritePending } = useWriteContract();
  const [selectedNft, setSelectedNft] = useState<NftData | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [nftImages, setNftImages] = useState<Record<string, string | null>>({});
  const [migratingTokenId, setMigratingTokenId] = useState<string | null>(null);
  const [migrateError, setMigrateError] = useState<string | null>(null);

  const v2MainReady =
    !!NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address && NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address !== ZERO;

  const { data: legacyV2Address } = useReadContract({
    address: NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address,
    abi: NFT_DAO_V2_MAIN_ABI,
    functionName: 'legacyV2',
    query: { enabled: v2MainReady },
  }) as { data: `0x${string}` | undefined };

  const hasLegacyBridge =
    !!legacyV2Address && legacyV2Address.toLowerCase() !== ZERO.toLowerCase();

  const {
    data: isApprovedForMigration,
    refetch: refetchLegacyApproval,
    isLoading: isLoadingLegacyApproval,
  } = useReadContract({
    address: legacyV2Address,
    abi: ERC721_APPROVAL_ABI,
    functionName: 'isApprovedForAll',
    args:
      userAccount && legacyV2Address && v2MainReady && hasLegacyBridge
        ? [userAccount, NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address]
        : undefined,
    query: {
      enabled: !!userAccount && !!legacyV2Address && v2MainReady && hasLegacyBridge,
    },
  }) as { data: boolean | undefined; refetch: () => void; isLoading: boolean };

  // Fetch user NFTs
  const { data: userNfts, isLoading: isLoadingNfts, refetch: refetchNfts } = useReadContract({
    address: NFT_DAO_UTILS_CONTRACT_CONFIG.address,
    abi: NFT_DAO_UTILS_CONTRACT_CONFIG.abi,
    functionName: 'getUserNfts',
    args: userAccount ? [userAccount] : undefined,
    query: {
      enabled: !!userAccount && !!NFT_DAO_UTILS_CONTRACT_CONFIG.address,
    },
  }) as { data: NftData[] | undefined; isLoading: boolean; refetch: () => void };

  // Fetch NFT count
  const { data: nftCount } = useReadContract({
    address: NFT_DAO_UTILS_CONTRACT_CONFIG.address,
    abi: NFT_DAO_UTILS_CONTRACT_CONFIG.abi,
    functionName: 'getUserNftCount',
    args: userAccount ? [userAccount] : undefined,
    query: {
      enabled: !!userAccount && !!NFT_DAO_UTILS_CONTRACT_CONFIG.address,
    },
  }) as { data: bigint | undefined };

  // Find oldest NFT mint date
  const oldestMintTime = useMemo(() => {
    if (!userNfts || userNfts.length === 0) return null;
    return userNfts.reduce((oldest, nft) => {
      return nft.mintTime < oldest ? nft.mintTime : oldest;
    }, userNfts[0].mintTime);
  }, [userNfts]);

  // Generate reward keys from oldest mint time to current month
  const rewardKeys = useMemo(() => {
    if (!oldestMintTime) return [];
    return generateRewardKeys(oldestMintTime);
  }, [oldestMintTime]);

  // Group reward keys by year
  const rewardKeysByYear = useMemo(() => {
    return groupRewardKeysByYear(rewardKeys);
  }, [rewardKeys]);

  // Component to fetch and display reward summary for each period (compact month row)
  const RewardMonthRow = ({ periodKey }: { periodKey: string }) => {
    const { data: summaryData, isLoading } = useReadContract({
      address: NFT_DAO_UTILS_CONTRACT_CONFIG.address,
      abi: NFT_DAO_UTILS_CONTRACT_CONFIG.abi,
      functionName: 'getUserRewardSummary',
      args: userAccount ? [userAccount, periodKey] : undefined,
      query: {
        enabled: !!userAccount && !!NFT_DAO_UTILS_CONTRACT_CONFIG.address,
      },
    }) as { data: [bigint, bigint, bigint, bigint, bigint, bigint] | undefined; isLoading: boolean };

    // Transform the tuple response to RewardSummary object
    const summary: RewardSummary | null = summaryData ? {
      totalReward: summaryData[0],
      totalRewardLRSN: summaryData[1],
      claimableReward: summaryData[2],
      claimableRewardLRSN: summaryData[3],
      eligibleCount: summaryData[4],
      claimedCount: summaryData[5],
    } : null;

    if (isLoading) {
      return (
        <div className="flex items-center justify-between py-2 px-3 border-b border-border/50 last:border-b-0">
          <span className="text-sm font-medium text-muted-foreground">{getMonthName(periodKey)}</span>
          <RiLoader4Line className="size-4 animate-spin text-[#FF8A3C]" />
        </div>
      );
    }

    if (!summary) {
      return null;
    }

    // Only show periods with rewards
    if (summary.totalReward === BigInt(0) && summary.totalRewardLRSN === BigInt(0)) {
      return null;
    }

    const isAllClaimed = summary.claimedCount === summary.eligibleCount && summary.eligibleCount > BigInt(0);
    const hasClaimable = summary.claimableReward > BigInt(0) || summary.claimableRewardLRSN > BigInt(0);

    return (
      <div className="flex items-center justify-between py-2.5 px-3 border-b border-border/50 last:border-b-0 hover:bg-background/50 transition-colors">
        {/* Left side: Month name */}
        <div className="flex items-center gap-2 min-w-[120px]">
          <span className="text-sm font-medium">{getMonthName(periodKey)}</span>
          {isAllClaimed && (
            <Badge variant="outline" className="h-5 px-1.5 text-xs bg-green-500/10 text-green-400 border-green-500/30">
              <RiCheckLine className="size-3 mr-1" />
              Claimed
            </Badge>
          )}
        </div>

        {/* Right side: Compact details */}
        <div className="flex items-center gap-4 text-xs flex-1 justify-end">
          <div className="text-right min-w-[70px]">
            <div className="text-muted-foreground">Total</div>
            <div className="font-medium text-[#FF8A3C]">
              {parseFloat(formatEther(summary.totalReward)).toFixed(4)} LRS
            </div>
            <div className="font-medium text-[#FF8A3C]">
              {parseFloat(formatEther(summary.totalRewardLRSN)).toFixed(4)} LRSN
            </div>
          </div>
          {hasClaimable && (
            <div className="text-right min-w-[70px] border-l border-border/50 pl-4">
              <div className="text-muted-foreground">Claimable</div>
              <div className="font-medium">
                {parseFloat(formatEther(summary.claimableReward)).toFixed(4)} LRS
              </div>
              <div className="font-medium">
                {parseFloat(formatEther(summary.claimableRewardLRSN)).toFixed(4)} LRSN
              </div>
            </div>
          )}
          <div className="text-right min-w-[80px] border-l border-border/50 pl-4">
            <div className="text-muted-foreground">NFTs</div>
            <div className="font-medium">
              {summary.eligibleCount.toString()} eligible
            </div>
            <div className="text-muted-foreground">
              {summary.claimedCount.toString()} claimed
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Fetch NFT images from metadata
  useEffect(() => {
    if (!userNfts || userNfts.length === 0) {
      setNftImages({});
      return;
    }

    const fetchImages = async () => {
      const imageMap: Record<string, string | null> = {};

      await Promise.all(
        userNfts.map(async (nft) => {
          const tokenId = nft.tokenId.toString();
          try {
            let url = nft.tokenURI;

            // Handle IPFS URLs
            if (url.startsWith('ipfs://')) {
              url = `https://ipfs.io/ipfs/${url.replace('ipfs://', '')}`;
            }

            // Check if URL is a JSON file or if we need to fetch JSON
            const isJsonUrl = url.toLowerCase().endsWith('.json') || url.toLowerCase().includes('.json');

            let metadata: any;

            if (isJsonUrl) {
              // URL is already a JSON file, fetch and parse it
              const response = await fetch(url);
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              // Check if response is JSON
              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                metadata = await response.json();
              } else {
                // Try to parse as JSON anyway
                const text = await response.text();
                try {
                  metadata = JSON.parse(text);
                } catch (parseError) {
                  throw new Error('Response is not valid JSON');
                }
              }
            } else {
              // Regular URL, fetch and parse as JSON
              const response = await fetch(url);
              if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
              }

              const contentType = response.headers.get('content-type');
              if (contentType && contentType.includes('application/json')) {
                metadata = await response.json();
              } else {
                // Try to parse as JSON anyway
                const text = await response.text();
                try {
                  metadata = JSON.parse(text);
                } catch (parseError) {
                  throw new Error('Response is not valid JSON');
                }
              }
            }

            // Extract image URL from metadata
            if (metadata && metadata.image) {
              let imageUrl = metadata.image;
              if (imageUrl.startsWith('ipfs://')) {
                imageUrl = `https://ipfs.io/ipfs/${imageUrl.replace('ipfs://', '')}`;
              }
              imageMap[tokenId] = imageUrl;
            } else {
              imageMap[tokenId] = null;
            }
          } catch (error) {
            console.error(`Error fetching image for NFT ${tokenId}:`, error);
            imageMap[tokenId] = null;
          }
        })
      );

      setNftImages(imageMap);
    };

    fetchImages();
  }, [userNfts]);

  const handleNftClick = (nft: NftData) => {
    setSelectedNft(nft);
    setIsSidebarOpen(true);
  };

  const handleMigrateToV2 = async (nft: NftData, e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setMigrateError(null);
    if (!userAccount || !legacyV2Address || !v2MainReady) return;
    const id = nft.tokenId;
    setMigratingTokenId(id.toString());
    try {
      if (!isApprovedForMigration) {
        const approveHash = await writeContractAsync({
          address: legacyV2Address,
          abi: ERC721_APPROVAL_ABI,
          functionName: 'approve',
          args: [NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address, id],
        });
        await waitForTransactionReceipt(wagmiConfig, { hash: approveHash });
        await refetchLegacyApproval();
      }
      const migrateHash = await writeContractAsync({
        address: NFT_DAO_V2_MAIN_CONTRACT_CONFIG.address,
        abi: NFT_DAO_V2_MAIN_ABI,
        functionName: 'migrateFromV2',
        args: [id],
      });
      await waitForTransactionReceipt(wagmiConfig, { hash: migrateHash });
      await refetchNfts();
    } catch (err) {
      setMigrateError(err instanceof Error ? err.message : 'Migration failed');
    } finally {
      setMigratingTokenId(null);
    }
  };

  const formatAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const formatDate = (timestamp: bigint) => {
    return new Date(Number(timestamp) * 1000).toLocaleDateString();
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <div className="px-4 md:px-6 lg:px-8 @container">
          <div className="w-full max-w-6xl mx-auto">
            <header className="flex flex-wrap gap-3 min-h-20 py-4 shrink-0 items-center transition-all ease-linear border-b">
              <div className="flex flex-1 items-center gap-2">
                <SidebarTrigger className="-ms-1" />
                <div className="max-lg:hidden lg:contents">
                  <Separator
                    orientation="vertical"
                    className="me-2 data-[orientation=vertical]:h-4"
                  />
                  <Breadcrumb>
                    <BreadcrumbList>
                      <BreadcrumbItem className="hidden md:block">
                        <BreadcrumbLink asChild>
                          <Link to="/">Home</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbLink asChild>
                          <Link to="/nfts">NFTs</Link>
                        </BreadcrumbLink>
                      </BreadcrumbItem>
                      <BreadcrumbSeparator className="hidden md:block" />
                      <BreadcrumbItem>
                        <BreadcrumbPage>NFT V1</BreadcrumbPage>
                      </BreadcrumbItem>
                    </BreadcrumbList>
                  </Breadcrumb>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <ConnectButton />
              </div>
            </header>

            <div className="py-6">
              <div className="flex items-center mb-6">
                <div className="w-1 h-8 bg-[#FF8A3C] mr-3"></div>
                <h1 className="text-3xl font-bold">NFT V1</h1>
              </div>

              <div className="w-full max-w-xs h-1 bg-gradient-to-r from-[#FF8A3C] to-transparent mb-8 rounded-full"></div>

              {v2MainReady && hasLegacyBridge && userAccount && (
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 rounded-lg border border-border bg-sidebar/60 px-4 py-3">
                  <p className="text-sm text-muted-foreground">
                    Migrate this collection to{' '}
                    <Link to="/nfts/v2" className="text-[#FF8A3C] hover:underline font-medium inline-flex items-center gap-1">
                      NFT V2
                      <RiArrowRightLine className="size-4 shrink-0" aria-hidden />
                    </Link>
                    . Each card can migrate individually; approve the V2 contract for that token if needed.
                  </p>
                </div>
              )}

              {migrateError && (
                <div className="mb-6 text-sm text-red-400/90 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2">
                  {migrateError}
                </div>
              )}

              {/* Main Content: Reward Summary (40%) + NFTs Grid (60%) */}
              <div className="flex flex-col lg:flex-row gap-6 mb-8">
                {/* Reward Summary Section - 40% */}
                <div className="w-full lg:w-[40%]">
                  {userAccount && userNfts && userNfts.length > 0 && Object.keys(rewardKeysByYear).length > 0 ? (
                    <Card className="bg-sidebar border border-border h-full">
                      <CardHeader>
                        <CardTitle className="text-xl flex items-center gap-2">
                          <RiCoinsLine className="size-6 text-[#FF8A3C]" />
                          Reward Summary
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {Object.keys(rewardKeysByYear)
                            .sort((a, b) => parseInt(b, 10) - parseInt(a, 10)) // Latest year first
                            .map((year) => {
                              const monthKeys = rewardKeysByYear[year];
                              const hasRewards = monthKeys.length > 0;

                              if (!hasRewards) return null;

                              return (
                                <Collapsible key={year} defaultOpen={year === new Date().getFullYear().toString()}>
                                  <CollapsibleTrigger className="w-full">
                                    <div className="flex items-center justify-between w-full py-3 px-4 bg-background rounded-lg border border-border hover:border-[#FF8A3C]/50 transition-colors">
                                      <div className="flex items-center gap-2">
                                        <span className="text-lg font-semibold text-[#FF8A3C]">{year}</span>
                                        <span className="text-xs text-muted-foreground">
                                          ({monthKeys.length} {monthKeys.length === 1 ? 'month' : 'months'})
                                        </span>
                                      </div>
                                      <RiArrowDownSLine className="size-5 text-muted-foreground transition-transform duration-200 data-[state=open]:rotate-180" />
                                    </div>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <div className="mt-2 bg-sidebar rounded-lg border border-border overflow-hidden">
                                      {monthKeys
                                        .reverse() // Latest month first within year
                                        .map((key) => (
                                          <RewardMonthRow key={key} periodKey={key} />
                                        ))}
                                    </div>
                                  </CollapsibleContent>
                                </Collapsible>
                              );
                            })}
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-6 text-center">
                        <RiCoinsLine className="size-12 text-muted-foreground mx-auto mb-2" />
                        <p className="text-sm text-muted-foreground">No reward data available</p>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* NFTs Grid Section - 60% */}
                <div className="w-full lg:w-[60%]">
                  {!userAccount ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiWallet3Line className="size-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">Please connect your wallet to view your NFTs</p>
                      </CardContent>
                    </Card>
                  ) : isLoadingNfts ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiLoader4Line className="size-12 animate-spin text-[#FF8A3C] mx-auto mb-4" />
                        <p className="text-muted-foreground">Loading your NFTs...</p>
                      </CardContent>
                    </Card>
                  ) : !userNfts || userNfts.length === 0 ? (
                    <Card className="bg-sidebar border border-border">
                      <CardContent className="p-12 text-center">
                        <RiImageLine className="size-16 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No NFTs found in your wallet</p>
                      </CardContent>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {userNfts.map((nft) => {
                        const tokenId = nft.tokenId.toString();
                        const imageUrl = nftImages[tokenId];
                        return (
                          <Card
                            key={tokenId}
                            className="bg-sidebar border border-border hover:border-[#FF8A3C]/50 transition-colors cursor-pointer"
                            onClick={() => handleNftClick(nft)}
                          >
                            <CardContent className="px-4">
                              <div className="bg-muted rounded-lg mb-3 flex items-center justify-center overflow-hidden aspect-square">
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={`NFT #${tokenId}`}
                                    className="w-full h-full object-contain"
                                    onError={() => {
                                      setNftImages(prev => ({ ...prev, [tokenId]: null }));
                                    }}
                                  />
                                ) : (
                                  <RiImageLine className="size-16 text-muted-foreground" />
                                )}
                              </div>
                              <div className="space-y-2">
                                <div className="flex items-center justify-between">
                                  <span className="text-sm font-medium">#{tokenId}</span>
                                  <Badge variant="outline" className="bg-[#FF8A3C]/10 text-[#FF8A3C] border-[#FF8A3C]">
                                    L{nft.level.toString()}
                                  </Badge>
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  Minted: {formatDate(nft.mintTime)}
                                </div>
                                {v2MainReady && hasLegacyBridge && userAccount && (
                                  <Button
                                    type="button"
                                    variant="secondary"
                                    size="sm"
                                    disabled={
                                      migratingTokenId !== null ||
                                      isWritePending ||
                                      isLoadingLegacyApproval
                                    }
                                    className="w-full mt-2 cursor-pointer border-[#FF8A3C]/35 bg-[#FF8A3C]/10 text-[#FF8A3C] hover:bg-[#FF8A3C]/20"
                                    onClick={(e) => void handleMigrateToV2(nft, e)}
                                  >
                                    {migratingTokenId === tokenId ? (
                                      <>
                                        <RiLoader4Line className="size-4 animate-spin mr-2 inline" />
                                        Processing…
                                      </>
                                    ) : (
                                      <>
                                        <RiSwapLine className="size-4 mr-2 inline" />
                                        Migrate to V2
                                      </>
                                    )}
                                  </Button>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </SidebarInset>

      {/* NFT Detail Sidebar */}
      <NftDetailSidebar
        isOpen={isSidebarOpen}
        nft={selectedNft}
        disableRewardClaim
        onClose={() => {
          setIsSidebarOpen(false);
          setSelectedNft(null);
        }}
      />
    </SidebarProvider>
  );
}
